import type { DivisionWithClasses } from '@/api/hooks'
import type { TExhibitorWithEntries, TShow } from '@schemas'

export type SectionStatus = 'not_started' | 'in_progress' | 'complete'
export type SectionIndicatorStatus = SectionStatus | 'loading' | 'unknown'

export type RunSectionStatuses = {
  entries: SectionStatus
  judging: SectionStatus
  awards: SectionStatus
  reports: SectionStatus
}

export type JudgingProgressSummary = {
  total: number
  actionable: number
  started: number
  finalized: number
  actionableFinalized: number
  noAwards: number
}

function defaultStatuses(): RunSectionStatuses {
  return {
    entries: 'not_started',
    judging: 'not_started',
    awards: 'not_started',
    reports: 'not_started',
  }
}

/** The classes with counted entries and how many counted entries there are. */
export type EnteredClasses = {
  classIds: string[]
  entriesCount: number
}

type SummarizeJudgingProgressOptions = {
  includedClassIds?: Set<string>
}

type ClassIdentifierSource = { id?: string | null; code?: string | null }

function getClassIdentifier(source: ClassIdentifierSource | null | undefined): string | null {
  if (!source) return null
  const id = typeof source.id === 'string' ? source.id.trim() : ''
  if (id) return id
  const code = typeof source.code === 'string' ? source.code.trim() : ''
  return code || null
}

/** Summarizes an entrant list the way show stats count entries: not-entered entries are left out. */
export function summarizeEntrantList(exhibitors: TExhibitorWithEntries[]): EnteredClasses {
  const classIds = new Set<string>()
  let entriesCount = 0
  for (const exhibitor of exhibitors) {
    for (const entry of exhibitor.entries ?? []) {
      if (entry.status === 'not_entered') continue
      entriesCount += 1
      const identifier = getClassIdentifier(entry.class)
      if (identifier) classIds.add(identifier)
    }
  }
  return { classIds: Array.from(classIds), entriesCount }
}

export function summarizeJudgingProgress(
  divisions?: DivisionWithClasses[] | null,
  options?: SummarizeJudgingProgressOptions
): JudgingProgressSummary {
  const rawClasses = (divisions ?? []).flatMap(division => division.classes ?? [])
  const includedClassIds = options?.includedClassIds
  const allClasses = rawClasses
    .filter((showClass): showClass is NonNullable<(typeof rawClasses)[number]> =>
      Boolean(showClass)
    )
    .filter(showClass => {
      if (!includedClassIds) return true
      if (includedClassIds.size === 0) return false
      const identifier = getClassIdentifier(showClass)
      return identifier ? includedClassIds.has(identifier) : false
    })

  let actionable = 0
  let started = 0
  let finalized = 0
  let actionableFinalized = 0
  let noAwards = 0

  for (const showClass of allClasses) {
    const isNoAwards = Boolean(showClass.noAwards)
    const hasPlacements = showClass.hasPlacements === true
    const hasStarted = Boolean(
      showClass.judgingStartedAt || showClass.resultsFinalizedAt || hasPlacements
    )
    const isFinalized = Boolean(showClass.resultsFinalizedAt)

    if (isNoAwards) {
      noAwards += 1
    } else {
      actionable += 1
      if (isFinalized) {
        actionableFinalized += 1
      }
    }

    if (hasStarted) {
      started += 1
    }

    if (isFinalized) {
      finalized += 1
    }
  }

  return {
    total: allClasses.length,
    actionable,
    started,
    finalized,
    actionableFinalized,
    noAwards,
  }
}

export function computeRunSectionStatuses({
  show,
  divisions,
  entered,
}: {
  show?: TShow | null
  divisions?: DivisionWithClasses[] | null
  entered?: EnteredClasses | null
}): RunSectionStatuses {
  if (!show) {
    return defaultStatuses()
  }

  const enteredClassIds = new Set(entered?.classIds)
  const progress = summarizeJudgingProgress(divisions, { includedClassIds: enteredClassIds })
  const actionableFinalized = progress.actionableFinalized
  const noAwardsFinalized = Math.max(progress.finalized - actionableFinalized, 0)
  const actionableComplete = progress.actionable === 0 || actionableFinalized >= progress.actionable
  const noAwardsComplete = progress.noAwards === 0 || noAwardsFinalized >= progress.noAwards
  const hasClasses = progress.total > 0
  const totalEntries = entered?.entriesCount ?? 0

  let entriesStatus: SectionStatus
  if (show.status === 'judging' || show.status === 'finalized') {
    entriesStatus = 'complete'
  } else if (show.status === 'accepting_entries') {
    entriesStatus = 'in_progress'
  } else if (totalEntries > 0) {
    entriesStatus = 'in_progress'
  } else {
    entriesStatus = 'not_started'
  }

  let judgingStatus: SectionStatus
  if (show.status === 'finalized') {
    judgingStatus = 'complete'
  } else if (show.status === 'judging') {
    const anythingStarted = progress.started > 0
    if (!anythingStarted && hasClasses) {
      judgingStatus = 'in_progress'
    } else if (actionableComplete && noAwardsComplete) {
      judgingStatus = hasClasses ? 'complete' : 'in_progress'
    } else {
      judgingStatus = 'in_progress'
    }
  } else if (!hasClasses) {
    judgingStatus = 'not_started'
  } else if (progress.actionable === 0) {
    if (!noAwardsComplete) {
      judgingStatus = progress.started > 0 ? 'in_progress' : 'not_started'
    } else {
      judgingStatus = 'complete'
    }
  } else if (progress.started === 0) {
    judgingStatus = 'not_started'
  } else if (actionableComplete) {
    judgingStatus = 'complete'
  } else {
    judgingStatus = 'in_progress'
  }

  const awardsStatus: SectionStatus =
    show.status === 'finalized'
      ? 'complete'
      : show.status === 'judging'
        ? 'in_progress'
        : 'not_started'

  const reportsStatus: SectionStatus =
    show.status === 'finalized'
      ? 'complete'
      : progress.finalized > 0
        ? 'in_progress'
        : 'not_started'

  return {
    entries: entriesStatus,
    judging: judgingStatus,
    awards: awardsStatus,
    reports: reportsStatus,
  }
}

export function getSectionStatusLabel(status: SectionStatus): string {
  switch (status) {
    case 'complete':
      return 'Complete'
    case 'in_progress':
      return 'In progress'
    case 'not_started':
    default:
      return 'Not started'
  }
}
